import {
  Sheet,
  RunCourseRow,
  ReportRouteSlim,
  RunCourseSlim,
  RunCourseReport,
  RouteConfig,
} from '@/models/runCourse';
import { reactive, computed } from '@vue/composition-api';
import {
  getKpRoutes,
  fillKpRoadNameShort,
  getReportRoutes,
  getDebugRunCourseInfo,
} from '@/lib/runCourse/runCourseHelper';
import { haversineDistance } from '@/lib/geoCalcUtil';
import { dtFormat } from '@/lib/dateHelper';
import { NEXT_LINE_REFERENCE } from '@/consts/runCourse';
import type { Kilopost } from '@/models/apis/device/adminDeviceResponse';

interface RunCourseSheetsState {
  group1Id: number;
  report: RunCourseReport | null;
  kps: Kilopost[];
  runCourseSlim: RunCourseSlim;
  maxRowsInPage: number;
  maxRouteElementsInLine: number;
  createdWorkRoutes: boolean;
}

export interface RunCourseSheetsParameters {
  group1Id: number;
  kps: Kilopost[];
  report: RunCourseReport;
  maxRowsInPage: number;
  maxRouteElementsInLine: number;
}

export interface RunCourseSheetsResult {
  setRunCourse: () => void;
  getRouteConfig: () => RouteConfig | null;
  getSheets: () => Sheet[];
  calcRouteElementsNumRows: (routeElementsNum: number) => number;
  getRouteSheets: () => Sheet[];
  splitSheets: (sheets: Sheet[]) => Sheet[];
  getDebugInfo: () => string;
}

export function useRunCourseSheets(parameters: RunCourseSheetsParameters): RunCourseSheetsResult {
  const { group1Id, report, kps, maxRowsInPage, maxRouteElementsInLine } = parameters;
  const sheetsState = reactive<RunCourseSheetsState>({
    group1Id,
    report,
    kps,
    runCourseSlim: {
      routes: [],
    },
    maxRowsInPage,
    maxRouteElementsInLine,
    createdWorkRoutes: !!report?.workRoutes,
  });

  const workRoutes = sheetsState.report?.workRoutes ?? '[]';

  const hasWorkRoutes = computed<boolean>(() => {
    return workRoutes !== '' && workRoutes !== '[]';
  });

  // 時間範囲に基づいて距離を計算する関数
  const getDistanceByTimeRange = (startTime: Date | string, endTime: Date | string): string => {
    if (!sheetsState.report?.mtxsExt) return '0.00';

    const startTs = typeof startTime === 'string' ? new Date(startTime) : startTime;
    const endTs = typeof endTime === 'string' ? new Date(endTime) : endTime;
    const mtxList = sheetsState.report.mtxsExt;
    const rangeMtxList = mtxList.filter(mtx => {
      const mtxTime = new Date(mtx.ts);
      return mtxTime >= startTs && mtxTime <= endTs;
    });

    const distance = rangeMtxList.reduce((acc, mtx, idx, arr) => {
      if (idx === 0) return acc;
      const distance = haversineDistance(arr[idx - 1], mtx);
      return acc + distance;
    }, 0);
    return distance > 0 ? `${Math.round(distance / 10) / 100}` : '0.00';
  };

  const parseRunCourse = (rawRunCourse: string | null): RunCourseSlim => {
    const parsed: unknown = typeof rawRunCourse === 'string' ? JSON.parse(rawRunCourse) : rawRunCourse;
    if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as { routes?: unknown }).routes)) {
      return { routes: [] };
    }
    return parsed as RunCourseSlim;
  };

  const setRunCourse = (): void => {
    if (!sheetsState.report || !report.runCourse) return;

    try {
      const processedData = parseRunCourse(report.runCourse);
      fillKpRoadNameShort(sheetsState.group1Id, processedData);
      sheetsState.runCourseSlim = processedData;
    } catch {
      // 路線情報の読み込みに失敗
    }
  };

  const getRouteConfig = (): RouteConfig | null => {
    const sheets = getSheets();
    const rows = sheets.flatMap(e => e.rows);
    if (!rows || rows.length === 0) return null;

    const roadNameDisplayNames = rows.flatMap(row => row.routes.map(route => route.startRoadNameDisp));
    const kpRouteMap = getKpRoutes(sheetsState.group1Id, roadNameDisplayNames, sheetsState.kps);
    const routeType = 'main_line';
    const routeOptions = kpRouteMap[routeType] ?? [];
    return {
      kpRouteMap,
      routeType,
      routeOptions,
    };
  };

  // 時間範囲に基づいて時間の差を計算する関数
  const getTimeSpanByTimeRange = (startTime: Date | string, endTime: Date | string): string => {
    const totalSeconds = (new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000;
    if (totalSeconds <= 0) return '00:00';

    // 時間と分を計算
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    // HH:MM形式でフォーマット
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const splitRoutesByChunk = (
    routesInRange: ReportRouteSlim[],
    row: RunCourseRow,
  ): RunCourseRow[] => {
    const chunks: RunCourseRow[] = [];
    const maxRoutesPerChunk = sheetsState.maxRouteElementsInLine * sheetsState.maxRowsInPage;
    const totalRoutes = routesInRange.length;
    if (totalRoutes === 0) return [row];

    const totalDistanceDisp = getDistanceByTimeRange(routesInRange[0].startTs, routesInRange[totalRoutes - 1].startTs);
    const totalTimeSpanDisp = getTimeSpanByTimeRange(routesInRange[0].startTs, routesInRange[totalRoutes - 1].startTs);

    for (let i = 0; i < totalRoutes; i += maxRoutesPerChunk) {
      const isLastChunk = i + maxRoutesPerChunk >= totalRoutes;
      const splitRoutes = routesInRange.slice(i, i + maxRoutesPerChunk);
      const timeRangeStart = splitRoutes[0].startTs;
      const timeRangeEnd = splitRoutes[splitRoutes.length - 1].startTs;
      const chunk = {
        ...row,
        startTsDisp: dtFormat(timeRangeStart),
        endTsDisp: dtFormat(timeRangeEnd),
        routes: splitRoutes,
      };
      if (!chunk.distanceDisp) {
        chunk.distanceDisp = isLastChunk ? totalDistanceDisp : NEXT_LINE_REFERENCE;
      }
      if (!chunk.timeSpanDisp) {
        chunk.timeSpanDisp = isLastChunk ? totalTimeSpanDisp : NEXT_LINE_REFERENCE;
      }
      chunks.push(chunk);
    }

    return chunks;
  };

  const getSheets = (): Sheet[] => {
    if (!sheetsState.runCourseSlim.routes) return [];
    if (!sheetsState.report) return [];

    if (!hasWorkRoutes.value) {
      const routeSlims = getReportRoutes(sheetsState.runCourseSlim);
      if (!routeSlims || routeSlims.length === 0) return [];

      const rows = routeSlims.map((route, index) => ({
        num: index + 1,
        startTsDisp: route[0]?.startTs ?? '',
        endTsDisp: route[route.length - 1]?.startTs ?? '',
        timeSpanDisp: '',
        distanceDisp: '',
        bikou: '',
        routes: route,
      }));
      return [{ rows }];
    }

    try {
      const sheets: Sheet[] = JSON.parse(workRoutes);
      if (sheets && Array.isArray(sheets)) {
        return sheets;
      }
    } catch (error) {
      console.error('Failed to parse work_routes:', error);
      return [];
    }
    return [];
  };

  const splitRowByRoutes = (row: RunCourseRow): RunCourseRow[] => {
    if (!sheetsState.report) return [row];

    const chunks: RunCourseRow[] = [];

    // 時間範囲で分割
    const baseDepartureTs = sheetsState.report?.baseDepartureTs ?? '';
    const startTs = sheetsState.report?.startTs ?? '';
    const endTs = sheetsState.report?.endTs ?? '';
    const baseArrivalTs = sheetsState.report?.baseArrivalTs ?? '';
    const rowFirstRouteStartTs = row?.startTsDisp ?? '';
    const rowLastRouteStartTs = row?.endTsDisp ?? '';

    const timeRanges = [
      { start: new Date(baseDepartureTs), end: new Date(startTs), name: '基地出発〜作業開始' },
      { start: new Date(startTs), end: new Date(endTs), name: '作業開始〜作業終了' },
      { start: new Date(endTs), end: new Date(baseArrivalTs), name: '作業終了〜基地到着' },
    ];
    const routesByTimeRange: { [key: number]: ReportRouteSlim[] } = {};
    row.routes.forEach(route => {
      const routeTime = new Date(route.startTs);
      const timeRangeIndex = timeRanges.findIndex(
        range => routeTime >= range.start && routeTime < range.end,
      );
      if (timeRangeIndex !== -1) {
        if (!routesByTimeRange[timeRangeIndex]) {
          routesByTimeRange[timeRangeIndex] = [];
        }
        routesByTimeRange[timeRangeIndex].push(route);
      }
    });
    // 分割されたroutesを使って処理
    for (let i = 0; i < timeRanges.length; i++) {
      const routesInRange = routesByTimeRange[i] || [];
      if (routesInRange.length === 0) continue;
      // 時間範囲内のルートをさらにmaxRoutesPerChunkで分割
      const timeRangeChunks = splitRoutesByChunk(routesInRange, row);
      chunks.push(...timeRangeChunks);
    }
    // maxRoutesPerChunkで分割されていない場合、全てのルートを分割
    if (chunks.length === 0) {
      const splitChunks = splitRoutesByChunk(row.routes, row);
      chunks.push(...splitChunks);
    }
    // 基地出発〜作業開始の期間があれば追加
    if (!sheetsState.createdWorkRoutes && new Date(baseDepartureTs) < new Date(rowFirstRouteStartTs)) {
      const distanceDisp = getDistanceByTimeRange(baseDepartureTs, rowFirstRouteStartTs);
      const timeSpanDisp = getTimeSpanByTimeRange(baseDepartureTs, rowFirstRouteStartTs);

      chunks.unshift({
        num: 0,
        startTsDisp: baseDepartureTs,
        endTsDisp: row.routes[0].startTs,
        timeSpanDisp,
        distanceDisp,
        bikou: '',
        routes: [],
      });
    }
    // 作業終了〜基地到着の期間があれば追加
    if (!sheetsState.createdWorkRoutes && new Date(baseArrivalTs) > new Date(rowLastRouteStartTs)) {
      const distanceDisp = getDistanceByTimeRange(rowLastRouteStartTs, baseArrivalTs);
      const timeSpanDisp = getTimeSpanByTimeRange(rowLastRouteStartTs, baseArrivalTs);

      chunks.push({
        num: 0,
        startTsDisp: row.routes[row.routes.length - 1].startTs,
        endTsDisp: baseArrivalTs,
        timeSpanDisp,
        distanceDisp,
        bikou: '',
        routes: [],
      });
    }

    return chunks.length > 0 ? chunks : [row];
  };

  const calcRouteElementsNumRows = (routeElementsNum: number) => {
    // 経路のない行は高さを半分で計算する
    return routeElementsNum > 0 ? Math.ceil(routeElementsNum / sheetsState.maxRouteElementsInLine) : 0.5;
  };

  const getRouteSheets = (): Sheet[] => {
    if (!sheetsState.report) return [];
    if (!sheetsState.runCourseSlim || !sheetsState.runCourseSlim.routes) return [];

    const reportId = sheetsState.report.id;
    const initialSheets: Sheet[] = getSheets();
    const sheets = splitSheets(initialSheets);
    // v-for の :key 用 sheetID
    sheets.forEach((s, idx) => (s.sheetID = 'report' + reportId + 'sheet' + idx));
    if (!hasWorkRoutes.value) {
      let num = 0;
      sheets.forEach((sheet) => sheet.rows.forEach((row) => (row.num = ++num)));
      sheetsState.createdWorkRoutes = true;
    }
    return sheets;
  };

  const splitSheets = (sheets: Sheet[]): Sheet[] => {
    const newSheets: Sheet[] = [];
    let arr: RunCourseRow[] = [];
    let routeRowNum = 0;
    sheets.forEach((sheet) => {
      sheet.rows.forEach((row) => {
        const splitRows = !hasWorkRoutes.value ? splitRowByRoutes(row) : [row];
        splitRows.forEach((r) => {
          const rowNum = calcRouteElementsNumRows(r.routes.length);
          if (routeRowNum + rowNum > sheetsState.maxRowsInPage) {
            if (arr.length > 0) {
              newSheets.push({ rows: arr });
            }
            arr = [];
            routeRowNum = 0;
          }
          arr.push(r);
          routeRowNum += rowNum;
        });
      });
    });

    // 最後のページに残った行を追加
    if (arr.length > 0) {
      newSheets.push({ rows: arr });
    }

    // 最終ページに合計欄などをつける処理
    if (newSheets.length > 0) {
      const lastSheet = newSheets[newSheets.length - 1];
      lastSheet.hasLastDriveResult = true;
    }
    return newSheets;
  };

  const getDebugInfo = (): string => {
    if (!sheetsState.runCourseSlim) return 'No run course data available.';

    const debugInfo = getDebugRunCourseInfo(sheetsState.runCourseSlim);
    return debugInfo;
  };

  return {
    setRunCourse,
    getSheets,
    calcRouteElementsNumRows,
    getRouteSheets,
    splitSheets,
    getDebugInfo,
    getRouteConfig,
  };
}
